<template>
  <div class="warrant-list">
    <RkTable
      :data="warrantList"
      :loading="loading"
      :finished="finished"
      @row-click="rowClick"
      :defaultSort="{ prop: 'amount', order: 'desc' }"
      :showFooter="showFooter"
      @sort-change="sortChange"
      @load="getWarrantList"
    >
      <RkTableColumn prop="symbol" label="代码" fixed align="left" :width="`${postcssPatch(130)}`">
        <template v-if="showFilter" #header>
          <p class="filter-icon" @click="filterVisible = true">筛选<i class="iconfont icon-shaixuan"></i></p>
        </template>
        <template #default="{ row }">
          <div>
            <p class="one-line">{{ row.name }}</p>
            <p class="stock-symbol">{{ row.symbol ? row.symbol.toUpperCase() : '--' }}</p>
          </div>
        </template>
      </RkTableColumn>
      <RkTableColumn prop="price" :label="t('现价')" sortable :width="`${postcssPatch(80)}`">
        <template #default="{ row }">
          <p :style="{ color: $digitjs(row.change).color() }">{{ $digitjs(row.price).rounded(3) }}</p>
        </template>
      </RkTableColumn>
      <RkTableColumn prop="amount" :label="t('成交额')" sortable :width="`${postcssPatch(80)}`">
        <template #default="{ row }">
          <p>{{ $digitjs(row.amount).convert(2, 1) }}</p>
        </template>
      </RkTableColumn>
      <RkTableColumn prop="changeRate" :label="t('涨跌幅')" sortable :width="`${postcssPatch(80)}`">
        <template #default="{ row }">
          <p :style="{ color: $digitjs(row.changeRate).color() }">
            {{ row.changeRate > 0 ? '+' : null }}{{ $digitjs(row.changeRate).unit('%').rounded() }}
          </p>
        </template>
      </RkTableColumn>
      <RkTableColumn prop="volume" :label="t('成交量')" sortable :width="`${postcssPatch(80)}`">
        <template #default="{ row }">
          <p>{{ $digitjs(row.volume).convert(2, 1) }}</p>
        </template>
      </RkTableColumn>
      <RkTableColumn prop="strike" label="行使价" sortable :width="`${postcssPatch(100)}`">
        <template #default="{ row }">
          <p>{{ $digitjs(row.strike).rounded() }}</p>
        </template>
      </RkTableColumn>
      <RkTableColumn prop="maturityDate" label="到期日" sortable :width="`${postcssPatch(100)}`">
        <template #default="{ row }">
          <p>{{ row.maturityDate ? $dayjs(row.maturityDate).format('YYYY/MM/DD') : '--' }}</p>
        </template>
      </RkTableColumn>
      <RkTableColumn prop="outstandingRate" label="街货比" sortable :width="`${postcssPatch(100)}`">
        <template #default="{ row }">
          <p>{{ $digitjs(row.outstandingRate).unit('%').rounded() }}</p>
        </template>
      </RkTableColumn>
      <RkTableColumn prop="outstanding" label="街货量" sortable :width="`${postcssPatch(100)}`">
        <template #default="{ row }">
          <p>{{ $digitjs(row.outstanding).cut().convert(2, 1) }}</p>
        </template>
      </RkTableColumn>
      <RkTableColumn prop="bidVolume" label="买量" sortable :width="`${postcssPatch(100)}`">
        <template #default="{ row }">
          <p>{{ $digitjs(row.bidVolume).cut().convert(2, 1) }}</p>
        </template>
      </RkTableColumn>
      <RkTableColumn prop="askVolume" label="卖量" sortable :width="`${postcssPatch(100)}`">
        <template #default="{ row }">
          <p>{{ $digitjs(row.askVolume).cut().convert(2, 1) }}</p>
        </template>
      </RkTableColumn>
      <RkTableColumn prop="premium" label="溢价" sortable :width="`${postcssPatch(100)}`">
        <template #default="{ row }">
          <p>{{ $digitjs(row.premium).unit('%').rounded() }}</p>
        </template>
      </RkTableColumn>
      <RkTableColumn prop="moneyness" label="价内/价外" sortable :width="`${postcssPatch(100)}`">
        <template #default="{ row }">
          <p>{{ $digitjs(row.moneyness).unit('%').rounded() }}</p>
        </template>
      </RkTableColumn>
      <RkTableColumn prop="iv" label="引申波幅" sortable :width="`${postcssPatch(100)}`">
        <template #default="{ row }">
          <p>{{ $digitjs(row.iv).rounded() }}</p>
        </template>
      </RkTableColumn>
      <RkTableColumn prop="callLevel" label="收回价" sortable :width="`${postcssPatch(100)}`">
        <template #default="{ row }">
          <p>{{ $digitjs(row.callLevel).rounded() }}</p>
        </template>
      </RkTableColumn>
      <RkTableColumn prop="spotCallLevel" label="距收回价" sortable :width="`${postcssPatch(100)}`">
        <template #default="{ row }">
          <p>{{ $digitjs(row.spotCallLevel).unit('%').rounded() }}</p>
        </template>
      </RkTableColumn>
      <RkTableColumn prop="lowerStrikes" label="下限价" sortable :width="`${postcssPatch(100)}`">
        <template #default="{ row }">
          <p>{{ $digitjs(row.lowerStrikes).rounded() }}</p>
        </template>
      </RkTableColumn>
      <RkTableColumn prop="upperStrikes" label="上限价" sortable :width="`${postcssPatch(100)}`">
        <template #default="{ row }">
          <p>{{ $digitjs(row.upperStrikes).rounded() }}</p>
        </template>
      </RkTableColumn>
      <RkTableColumn prop="effectiveGearing" label="有效杠杆" sortable :width="`${postcssPatch(100)}`">
        <template #default="{ row }">
          <p>{{ $digitjs(row.effectiveGearing).rounded() }}</p>
        </template>
      </RkTableColumn>
      <RkTableColumn prop="gearingRate" label="杠杆比率" sortable :width="`${postcssPatch(100)}`">
        <template #default="{ row }">
          <p>{{ $digitjs(row.gearingRate).rounded() }}</p>
        </template>
      </RkTableColumn>
      <RkTableColumn prop="entRate" label="换股比率" sortable :width="`${postcssPatch(100)}`">
        <template #default="{ row }">
          <p>{{ $digitjs(row.entRate).rounded() }}</p>
        </template>
      </RkTableColumn>
      <RkTableColumn prop="delta" label="对冲值" sortable :width="`${postcssPatch(100)}`">
        <template #default="{ row }">
          <p>{{ $digitjs(row.delta).rounded(3) }}</p>
        </template>
      </RkTableColumn>
      <RkTableColumn prop="status" label="状态" sortable :width="`${postcssPatch(100)}`">
        <template #default="{ row }">
          <p>{{ statusMap[row.status] }}</p>
        </template>
      </RkTableColumn>

      <RkTableColumn prop="issuerName" label="发行人" :width="`${postcssPatch(150)}`">
        <template #default="{ row }">
          <p class="hidden-line">{{ row.issuerName }}</p>
        </template>
      </RkTableColumn>
      <RkTableColumn prop="stockOwnerName" label="相关股票" :width="`${postcssPatch(120)}`">
        <template #default="{ row }">
          <p class="hidden-line">{{ row.stockOwnerName ?? '--' }}</p>
        </template>
      </RkTableColumn>
    </RkTable>
    <search-filter v-model="filterVisible" :data="filterParams" @reset="resetParams" @load="loadList" />
  </div>
</template>

<script>
import SearchFilter from '@/components/warrants/SearchFilter.vue'
import { sendMsg } from '@/plugins/msg'
import { i18nTranslateInit } from '@/utils/utility-lang'
import { postcssPatch } from '@/utils/postcssPatch'
export default {
  name: 'Warrants',
  components: { SearchFilter },
  props: {
    total: {
      type: Number
    },
    showFilter: {
      type: Boolean,
      default: false
    },
    showFooter: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      t: i18nTranslateInit.t,
      filterVisible: false,
      loading: false,
      finished: false,
      warrantList: [],
      statusMap: {
        S: '终止交易',
        N: '正常',
        W: '待上市'
      },
      filterParams: {
        types: [], // 类型
        issuers: [], // 发行人
        maturityDate: [null, null], // 到期日
        moneyness: null, // 价内/价外
        gearingRate: [null, null], // 杠杆比率
        outstandingRate: [null, null], // 街货比
        premiumLow: '', // 溢价
        premiumHigh: ''
      },
      pageNum: 0,
      // pageSize: 30,
      sortField: 'amount', // 字段排序
      sortType: 1
    }
  },
  computed: {
    symbol() {
      return this.$route.query.symbol
    },
    pageSize() {
      return this.total ?? 30
    }
  },
  watch: {
    symbol: {
      immediate: true,
      handler() {
        this.resetParams()
        this.reSearchList()
        this.getWarrantList()
      }
    }
  },
  methods: {
    postcssPatch,
    reSearchList() {
      this.sortField = 'amount'
      this.sortType = 1
      this.pageNum = 0
      this.warrantList = []
      this.finished = false
    },
    sortChange({ prop, order }) {
      this.sortField = prop
      this.sortType = order === 'asc' ? 0 : 1
      this.pageNum = 0
      this.warrantList = []
      this.finished = false
      this.getWarrantList()
    },
    async getWarrantList() {
      this.loading = true

      // 处理到期日参数
      const [maturityStartDate, maturityEndDate] = this.filterParams.maturityDate.map((item) => {
        return item ? this.$dayjs().add(item, 'month').format('YYYY-MM-DD') : item
      })
      // 处理杠杆比率
      const [gearingRateLow, gearingRateHigh] = this.filterParams.gearingRate
      // 处理街货比
      const [outstandingRateLow, outstandingRateHigh] = this.filterParams.outstandingRate
      // 处理类型、发行人、价内/价外、溢价
      const { types, issuers, moneyness, premiumLow, premiumHigh } = this.filterParams

      this.pageNum++
      const res = await this.$post(
        this.$api.sdk.hk + '/warrant/list',
        {
          types, // 类型
          issuers, // 发行人
          maturityStartDate, // 到期日
          maturityEndDate,
          moneyness, // 价内/价外
          gearingRateLow, // 杠杆比率
          gearingRateHigh,
          outstandingRateLow, // 街货比
          outstandingRateHigh,
          premiumLow, // 溢价
          premiumHigh,
          pageNum: this.pageNum,
          pageSize: this.pageSize,
          sortField: this.sortField, // 字段排序
          sortType: this.sortType,
          symbol: this.symbol
        },
        {
          headers: {
            'Org-Data-Enable': 'false'
          }
        }
      )
      const list = res.data?.warrants?.records ?? []
      const isLastPage = res.data?.warrants?.current === res.data?.warrants.pages
      this.warrantList = [...this.warrantList, ...(list ?? [])]
      this.finished = this.total ? true : isLastPage ?? true
      this.loading = false
    },
    resetParams() {
      this.filterParams = this.$options.data().filterParams
    },
    loadList(data) {
      this.filterParams = JSON.parse(JSON.stringify(data))
      this.reSearchList()
      this.getWarrantList()
      this.filterVisible = false
    },
    rowClick(row) {
      sendMsg({
        event: 'hkWarrantClick',
        symbol: row.symbol,
        type: row.type
      })
    }
  }
}
</script>

<style lang="scss" scoped>
.warrant-list {
  .stock-symbol {
    padding-top: 10px;
    font-size: 12px;
    color: var(--font-color-3);
  }
  .filter-icon {
    font-size: 16px;
    color: var(--main-color-1);
  }
  .hidden-line {
    display: -webkit-box;
    overflow: hidden;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 1;
    padding-left: 20px;
  }
  :deep(.rk-table .rk-table__row--body) {
    border-bottom: 1px solid var(--border-color-1);
  }
}
</style>
